/**
 * Cálculos e tipos de linha para itens de proposta (grupos, subtotais, colunas calculadas).
 */

export const TIPO_LINHA = {
  DADOS: "dados",
  CABECALHO_GRUPO: "cabecalho_grupo",
  SUBTOTAL: "subtotal",
};

export function getTipoLinha(item) {
  if (!item || typeof item !== "object") return TIPO_LINHA.DADOS;
  const t = item.tipo_linha;
  if (t == null || t === "") return TIPO_LINHA.DADOS;
  if (t === TIPO_LINHA.DADOS || t === TIPO_LINHA.CABECALHO_GRUPO || t === TIPO_LINHA.SUBTOTAL) {
    return t;
  }
  // Extração / legado: "item", "linha", etc. — tratar como linha de dados
  return TIPO_LINHA.DADOS;
}

export function extrairNumero(valor) {
  if (valor === null || valor === undefined) return 0;
  if (typeof valor === "number") return valor;

  if (typeof valor === "string") {
    const numero = valor
      .replace(/R\$/g, "")
      .replace(/\s/g, "")
      .replace(/\./g, "")
      .replace(",", ".");
    const resultado = parseFloat(numero);
    return isNaN(resultado) ? 0 : resultado;
  }

  return 0;
}

/**
 * Substitui {col_id} pelos valores numéricos de `valoresPorId` e avalia expressão segura (+ - * / parênteses).
 */
export function avaliarFormula(formula, valoresPorId) {
  if (!formula || typeof formula !== "string") return 0;

  let expr = formula.replace(/\{([^}]+)\}/g, (_, id) => {
    const raw = valoresPorId[id];
    const n = extrairNumero(raw);
    return Number.isFinite(n) ? String(n) : "0";
  });

  expr = expr.replace(/\s+/g, "");
  if (!expr) return 0;

  // Apenas dígitos, separador decimal e operadores (após substituir {colunas})
  if (!/^[\d+\-*/().]+$/i.test(expr)) {
    return 0;
  }

  try {
    const fn = new Function(`"use strict"; return (${expr})`);
    const r = fn();
    return typeof r === "number" && Number.isFinite(r) ? r : 0;
  } catch {
    return 0;
  }
}

/**
 * Aplica colunas calculadas em ordem; retorna cópia do item com valores calculados preenchidos.
 */
export function aplicarColunasCalculadas(item, colunas) {
  if (!item || !colunas?.length) return { ...item };

  const vals = { ...item };

  for (const col of colunas) {
    if (col.tipo !== "calculada" || !col.formula?.trim()) continue;
    vals[col.id] = avaliarFormula(col.formula, vals);
  }

  return vals;
}

const EPS_LINHA_CALCULO = 1e-6;

/**
 * Espelha editalToPropostaItens.classificarColunaProposta (sem import circular).
 * @returns {'item'|'grupo'|'subgrupo'|'descricao'|'quantidade'|'unidade'|'valor_unitario'|'valor_total'|null}
 */
function semanticaColunaProposta(col) {
  if (!col || typeof col !== "object") return null;
  const id = String(col.id ?? "");
  const nome = String(col.nome ?? col.label ?? "").toLowerCase();
  const nomeId = `${nome} ${id}`;

  if (/subgrupo|sub-grupo|sub\s*grupo|sub_grupo/i.test(nomeId) || /^subgrupo$/i.test(id))
    return "subgrupo";
  if (
    id === "grupo" ||
    ((/\bgrupo\b|loteamento|\blote\b/i.test(nome)) && !/subgrupo/i.test(nomeId)) ||
    /^lote$/i.test(id) ||
    /^grp$/i.test(id)
  )
    return "grupo";

  if (
    /^item$/i.test(id) ||
    /^n[º°]?\s*item/i.test(nome) ||
    (/^item$/i.test(nome) && !/sub/i.test(nome))
  )
    return "item";

  if (id === "descricao" || /\bdescri/i.test(nome) || /\bespecifica/i.test(nome)) return "descricao";
  if (id === "quantidade" || (/\bquantidade\b/i.test(nome) && !/valor/i.test(nome)))
    return "quantidade";
  if (id === "unidade" || /\bund\b/i.test(nome) || /\bunidade\b/i.test(nome)) return "unidade";

  if (
    id === "valor_unitario" ||
    id === "valor_referencia" ||
    id === "valorUnitario" ||
    id === "valorReferencia" ||
    /\bvalor\s*unit/i.test(nome) ||
    /\bpre[cç]o\s*unit/i.test(nome) ||
    /\bv\.?\s*unit\b/i.test(nome)
  )
    return "valor_unitario";
  if (id === "valor_total" || /\bvalor\s*total\b/i.test(nome)) return "valor_total";

  return null;
}

/**
 * Ids reais das colunas para quantidade, valor unitário e valor total (incl. `col_*` da planilha/edital).
 */
export function resolverIdsCalculoAutomaticoPreco(colunas) {
  const fallback = { idQtd: "quantidade", idVu: "valor_unitario", idVt: "valor_total" };
  if (!colunas || colunas.length === 0) return fallback;

  /** @type {Record<string, string>} */
  const primeiroPorSem = {};
  for (const c of colunas) {
    const sem = semanticaColunaProposta(c);
    if (sem && !(sem in primeiroPorSem) && c.id != null && c.id !== "") {
      primeiroPorSem[sem] = c.id;
    }
  }

  let idQtd = primeiroPorSem.quantidade ?? null;
  let idVu = primeiroPorSem.valor_unitario ?? null;
  let idVt = primeiroPorSem.valor_total ?? null;

  const colunasNumero = colunas.filter(
    (c) =>
      c.tipo === "numero" &&
      String(c.id) !== "item" &&
      semanticaColunaProposta(c) !== "item"
  );
  const colunasValor = colunas.filter((c) => c.tipo === "valor");

  if (!idQtd && colunasNumero.length > 0) {
    idQtd =
      colunasNumero.find((c) => /quant|qtd|\bqde\b/i.test(String(c.nome ?? "")))?.id ??
      colunasNumero[0]?.id ??
      null;
  }

  if (!idVu && colunasValor.length > 0) {
    const candidatosVu = colunasValor.filter((c) => {
      const sem = semanticaColunaProposta(c);
      if (sem === "valor_total") return false;
      if (/\btotal\b/i.test(String(c.nome ?? ""))) return false;
      return true;
    });
    idVu = candidatosVu[0]?.id ?? colunasValor[0]?.id ?? null;
  }

  if (!idVt && colunasValor.length > 0) {
    const porNome =
      colunasValor.find(
        (c) =>
          semanticaColunaProposta(c) === "valor_total" || /\bvalor\s*total\b/i.test(String(c.nome ?? ""))
      )?.id ?? null;
    idVt = porNome ?? colunasValor[colunasValor.length - 1]?.id ?? null;
    if (idVt === idVu && colunasValor.length >= 2) {
      idVt = colunasValor[colunasValor.length - 1]?.id;
    }
  }

  return {
    idQtd: idQtd ?? fallback.idQtd,
    idVu: idVu ?? fallback.idVu,
    idVt: idVt ?? fallback.idVt,
  };
}

function extrairValorTotalLinhaComparacao(row, colunas) {
  const { idVu, idVt } = resolverIdsCalculoAutomaticoPreco(colunas);
  if (idVt && idVt !== idVu) {
    const v = row[idVt];
    if (v != null && v !== "") return extrairNumero(v);
  }
  return extrairNumero(row.valor_total);
}

/**
 * Recalcula valor total da linha (qtd × valor unit.) na coluna correta (`col_*` ou canónica) e colunas calculadas.
 */
export function recalcularLinhasDadosCalculoAutomatico(itens, colunas) {
  const { idQtd, idVu, idVt } = resolverIdsCalculoAutomaticoPreco(colunas);
  return (itens || []).map((row) => {
    if (getTipoLinha(row) !== TIPO_LINHA.DADOS) return row;
    if (idVu && idVt && idVu === idVt) {
      return aplicarColunasCalculadas({ ...row }, colunas);
    }

    const q = idQtd ? extrairNumero(row[idQtd]) : extrairNumero(row.quantidade);
    const vu = idVu ? extrairNumero(row[idVu]) : extrairNumero(row.valor_unitario);
    const total = q * vu;

    const withTotal = { ...row };
    if (idQtd) withTotal.quantidade = q;
    if (idVu) withTotal.valor_unitario = vu;
    if (idVt && idVt !== idVu) withTotal[idVt] = total;
    withTotal.valor_total = total;

    return aplicarColunasCalculadas(withTotal, colunas);
  });
}

/** Indica se o recálculo automático produziria linhas `dados` diferentes das atuais. */
export function itensLinhasDadosCalculoDivergem(itensAntes, itensDepois, colunas) {
  if (!itensAntes || !itensDepois || itensAntes.length !== itensDepois.length) return true;
  for (let i = 0; i < itensAntes.length; i++) {
    const a = itensAntes[i];
    const b = itensDepois[i];
    if (getTipoLinha(a) !== TIPO_LINHA.DADOS) continue;
    if (
      Math.abs(extrairValorTotalLinhaComparacao(a, colunas) - extrairValorTotalLinhaComparacao(b, colunas)) >
      EPS_LINHA_CALCULO
    ) {
      return true;
    }
    for (const col of colunas || []) {
      if (col.tipo !== "calculada") continue;
      if (Math.abs(extrairNumero(a[col.id]) - extrairNumero(b[col.id])) > EPS_LINHA_CALCULO) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Valor numérico exibido em uma célula (para somas e subtotais).
 */
export function valorNumericoCelula(item, coluna, colunas) {
  if (!coluna) return 0;
  const vals = aplicarColunasCalculadas(item, colunas);

  if (coluna.tipo === "calculada") {
    return extrairNumero(vals[coluna.id]);
  }
  if (coluna.tipo === "valor" || coluna.tipo === "numero") {
    return extrairNumero(vals[coluna.id]);
  }
  return 0;
}

/**
 * Soma da coluna para o subtotal na linha `indexSubtotal` (percorre linhas do mesmo grupo acima).
 */
export function somarColunaAteSubtotal(itens, indexSubtotal, coluna, colunas) {
  const sub = itens[indexSubtotal];
  if (!sub || getTipoLinha(sub) !== TIPO_LINHA.SUBTOTAL) return 0;

  const g = sub.grupo_id;
  if (g === undefined || g === null) return 0;

  let sum = 0;
  for (let i = indexSubtotal - 1; i >= 0; i--) {
    const row = itens[i];
    const tipo = getTipoLinha(row);
    if (tipo === TIPO_LINHA.CABECALHO_GRUPO && row.grupo_id === g) break;
    if (tipo === TIPO_LINHA.SUBTOTAL && row.grupo_id === g) break;
    if (tipo === TIPO_LINHA.DADOS && row.grupo_id === g) {
      sum += valorNumericoCelula(row, coluna, colunas);
    }
  }
  return sum;
}

/**
 * Valor total de uma linha de dados (para total geral da proposta).
 */
export function getValorLinhaParaTotalGeral(item, colunas) {
  if (getTipoLinha(item) !== TIPO_LINHA.DADOS) return 0;

  const vals = aplicarColunasCalculadas(item, colunas);
  const { idQtd, idVu, idVt } = resolverIdsCalculoAutomaticoPreco(colunas);

  const q = idQtd ? extrairNumero(vals[idQtd]) : extrairNumero(vals.quantidade);
  const vu = idVu ? extrairNumero(vals[idVu]) : extrairNumero(vals.valor_unitario);
  const produto = q * vu;

  const chaveVt = idVt && idVt !== idVu ? idVt : "valor_total";
  const rawVt = vals[chaveVt];
  if (rawVt != null && rawVt !== "") {
    const vt = extrairNumero(rawVt);
    // valor_total 0 com q×vu ≠ 0 costuma ser persistência incompleta; usar o produto
    if (vt !== 0 || produto === 0) return vt;
  }

  if (q !== 0 && vu !== 0) {
    return produto;
  }

  for (let i = colunas.length - 1; i >= 0; i--) {
    const c = colunas[i];
    if (c.tipo === "valor" || c.tipo === "calculada") {
      const v = extrairNumero(vals[c.id]);
      if (v !== 0) return v;
    }
  }

  return 0;
}

export function calcularTotalGeralItens(itens, colunas) {
  let total = 0;
  for (const item of itens || []) {
    total += getValorLinhaParaTotalGeral(item, colunas || []);
  }
  return total;
}

export function contarGrupos(itens) {
  return (itens || []).filter((r) => getTipoLinha(r) === TIPO_LINHA.CABECALHO_GRUPO).length;
}

/** Renumera apenas linhas `dados` (1, 2, 3…); demais tipos inalterados. */
export function renumerarLinhasDados(itens) {
  let n = 1;
  return (itens || []).map((row) => {
    if (getTipoLinha(row) !== TIPO_LINHA.DADOS) return row;
    return { ...row, item: n++ };
  });
}

export function ultimoGrupoIdDeCabecalho(itens) {
  for (let i = (itens || []).length - 1; i >= 0; i--) {
    const row = itens[i];
    if (getTipoLinha(row) === TIPO_LINHA.CABECALHO_GRUPO && row.grupo_id) {
      return row.grupo_id;
    }
  }
  return null;
}

/** Índice do último cabeçalho de grupo na lista (varre do fim). */
export function indiceUltimoCabecalhoGrupo(itens) {
  for (let i = (itens || []).length - 1; i >= 0; i--) {
    const row = itens[i];
    if (getTipoLinha(row) === TIPO_LINHA.CABECALHO_GRUPO && row.grupo_id) return i;
  }
  return -1;
}

/**
 * `grupo_id` para novas linhas de dados no **fim** da lista: só o último cabeçalho da lista,
 * e apenas se esse grupo ainda **não** foi fechado por um `SUBTOTAL` com o mesmo `grupo_id`.
 * Evita que «Adicionar item» ligue linhas órfãs (após subtotal) ao grupo anterior.
 */
export function ultimoGrupoIdAbertoParaNovosItens(itens) {
  const hIdx = indiceUltimoCabecalhoGrupo(itens);
  if (hIdx < 0) return null;
  const g = itens[hIdx].grupo_id;
  if (g === undefined || g === null || g === "") return null;
  for (let i = hIdx + 1; i < itens.length; i++) {
    const row = itens[i];
    if (getTipoLinha(row) === TIPO_LINHA.SUBTOTAL && row.grupo_id === g) {
      return null;
    }
  }
  return g;
}

/**
 * Última linha de dados contígua ao cabeçalho (mesmo grupo_id), até subtotal do grupo ou linha órfã.
 * Usado para inserir subtotal logo após o bloco real do grupo, não no fim da tabela.
 */
export function indiceUltimaLinhaDadosContiguaAoCabecalho(itens, indiceCabecalho) {
  const h = itens[indiceCabecalho];
  if (!h || getTipoLinha(h) !== TIPO_LINHA.CABECALHO_GRUPO) return indiceCabecalho;
  const g = h.grupo_id;
  let last = indiceCabecalho;
  for (let i = indiceCabecalho + 1; i < itens.length; i++) {
    const row = itens[i];
    const tipo = getTipoLinha(row);
    if (tipo === TIPO_LINHA.CABECALHO_GRUPO) break;
    if (tipo === TIPO_LINHA.SUBTOTAL && row.grupo_id === g) break;
    if (tipo === TIPO_LINHA.SUBTOTAL) break;
    if (tipo === TIPO_LINHA.DADOS && row.grupo_id === g) last = i;
    else if (tipo === TIPO_LINHA.DADOS) break;
  }
  return last;
}
